package com.eladfinish.bible.contest.screens.search.usecases.engine

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.DATE_SYMBOL
import com.eladfinish.bible.contest.constants.StringConstants.HASH_TAG_SYMBOL
import com.eladfinish.bible.contest.constants.StringConstants.NUN_MENUZERET
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OT
import com.eladfinish.bible.contest.extensions.asInnerRegex
import com.eladfinish.bible.contest.extensions.matches
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.screens.search.model.SearchNumberOrRoot
import com.eladfinish.bible.contest.screens.search.model.SearchSuggestion
import com.eladfinish.bible.contest.screens.search.usecases.date.ParseDateQueryResult
import com.eladfinish.bible.contest.screens.search.usecases.date.ParseDateQueryUseCase
import com.eladfinish.bible.contest.screens.search.usecases.engine.model.PrepareLocalSearchRequestInput
import com.eladfinish.bible.contest.screens.search.usecases.engine.model.PrepareLocalSearchRequestResult
import com.eladfinish.bible.contest.type_at_least_two_letters
import com.eladfinish.bible.contest.utils.MyStringUtils.getAlefTavExcluding
import com.eladfinish.bible.contest.utils.MyStringUtils.getSiman
import com.eladfinish.bible.contest.utils.MyStringUtils.getSimanNoDagesh
import dev.icerock.moko.resources.desc.desc

class PrepareLocalSearchRequestUseCase(
    private val parseDateQueryUseCase: ParseDateQueryUseCase = ParseDateQueryUseCase(),
) {

    operator fun invoke(
        input: PrepareLocalSearchRequestInput,
    ): PrepareLocalSearchRequestResult {
        val searchedText = input.searchedText
        var regex = searchedText
        val originalExpression = regex

        if (regex == DEBUG_USER_CODE) {
            return PrepareLocalSearchRequestResult.ToggleDebugUser
        }

        if (regex.startsWith(StringConstants.SHORESH)) {
            return PrepareLocalSearchRequestResult.ExecuteSpecial(
                searchNumberOrRoot = SearchNumberOrRoot.ROOT,
                regex = regex,
            )
        }

        if (regex.matches("\\d+") || regex.matches("!\\d+!")) {
            return PrepareLocalSearchRequestResult.SuggestAlternative(
                searchSuggestion = SearchSuggestion.USE_NUMBER_SIGN,
            )
        }

        if (regex.contains(NUN_MENUZERET)) {
            return PrepareLocalSearchRequestResult.Error(
                message = "חיפוש נ' מנוזרת נמצא בפיתוח".desc(),
            )
        }

        if (regex.contains(DATE_SYMBOL)) {
            return when (val date = parseDateQueryUseCase(regex)) {
                is ParseDateQueryResult.Success -> PrepareLocalSearchRequestResult.ExecuteSpecial(
                    searchNumberOrRoot = SearchNumberOrRoot.DATE,
                    regex = regex,
                    dateQuery = date.query,
                )

                is ParseDateQueryResult.Failure -> PrepareLocalSearchRequestResult.Error(
                    message = date.error.message.desc(),
                )
            }
        }

        if (regex.startsWith(HASH_TAG_SYMBOL)) {
            if (regex.matches("$HASH_TAG_SYMBOL\\d{1,9}")) {
                return PrepareLocalSearchRequestResult.ExecuteSpecial(
                    searchNumberOrRoot = SearchNumberOrRoot.NUMBER,
                    regex = regex,
                )
            }

            return PrepareLocalSearchRequestResult.Error(
                message = "יש להקליד סולמית ולאחר מכן מספר שלם (עד 9 ספרות)!".desc(),
            )
        }

        if (searchedText.length <= 1) {
            return PrepareLocalSearchRequestResult.Error(
                message = SharedRes.strings.type_at_least_two_letters.desc(),
            )
        }

        if (input.isDebugUser && originalExpression.matches("$OT,".asInnerRegex())) {
            val excludedLetter = originalExpression[originalExpression.indexOf(",") - 1]

            return PrepareLocalSearchRequestResult.DebugMessage(
                message = "ביטוי לאי-כלילת אות:\n" + getAlefTavExcluding(excludedLetter),
            )
        }

        if (originalExpression.matches("$OT${getSiman()}${StringConstants.DAGESH}".asInnerRegex())) {
            return PrepareLocalSearchRequestResult.Error(
                message = "יש להוסיף קודם דגש ורק אחריו ניקוד אחר!".desc(),
            )
        }

        val shin = "ש"
        val shinSemalit = "ׂ"
        val shinYemanit = "ׁ"

        if (originalExpression.matches("$shin${getSimanNoDagesh()}($shinSemalit|$shinYemanit)".asInnerRegex())) {
            return PrepareLocalSearchRequestResult.Error(
                message = "יש לציין קודם אם האות ש' היא ימנית או שמאלית ורק אחר כך להוסיף לה ניקוד!".desc(),
            )
        }

        val suggestions = buildList {
            if (regex.endsWith(" ")) {
                add(SearchSuggestion.WORD_BOUNDARY)
            }
        }

        if (input.searchMode in listOf(
                SearchMode.VERSE_FOR_INVITATION,
                SearchMode.FIRST_LAST_LETTERS_IN_NAME,
            )
        ) {
            regex = buildNameSearchRegex(originalExpression)
        }

        buildIntervalSearchRegexOrNull(originalExpression)?.let {
            regex = it
        }

        regex = regex.normalizeBibleSearchRegex()

        return PrepareLocalSearchRequestResult.Ready(
            originalExpression = originalExpression,
            normalizedRegex = regex,
            suggestions = suggestions,
        )
    }

    private fun buildNameSearchRegex(
        originalExpression: String,
    ): String {
        val nameLetters = originalExpression
            .filter { it in 'א'..'ת' }

        if (nameLetters.isEmpty()) {
            return ""
        }

        val intervalBetweenLetters = "~*?"

        return nameLetters
            .map { letter -> "($letter)$intervalBetweenLetters" }
            .joinToString(separator = "")
            .removeSuffix(intervalBetweenLetters)
    }

    private fun buildIntervalSearchRegexOrNull(
        expression: String,
    ): String? {
        val whitespaceRegex = Regex("""\s+""")
        val anyCharRegex = Regex("""(.)""")
        val dashIndex = expression.indexOf('-')

        if (dashIndex <= 0) return null

        val skip = expression
            .substring(startIndex = 0, endIndex = dashIndex)
            .toIntOrNull()
            ?: return null

        val intervalBetweenLetters = "(?:. ?){$skip}"

        val compactExpression = expression
            .substring(startIndex = dashIndex + 1)
            .replace(regex = whitespaceRegex, replacement = "")

        if (compactExpression.isEmpty()) return null

        val wrappedExpression = compactExpression.replace(
            regex = anyCharRegex,
            replacement = "($1)$intervalBetweenLetters",
        )

        return wrappedExpression.removeSuffix(suffix = intervalBetweenLetters)
    }

    private fun String.normalizeBibleSearchRegex(): String = this
        .replaceRegex(regex = "ה['׳]", replacement = "יהוה")
        .replaceRegex(
            regex = "(" + OT + ")" + StringConstants.RAFE,
            replacement = "(?:$1R)",
        )
        .replaceRegex(regex = "(" + getSiman() + "\\??" + ")", replacement = "@$1")
        .replaceRegex(regex = "(" + OT + "(?:" + getSiman() + "\\??)*" + ")", replacement = "(?:$1@)")
        .replace(oldValue = "[", newValue = "(?:")
        .replace(oldValue = "]", newValue = ")")

    private companion object {
        const val DEBUG_USER_CODE = "302725130"
    }
}
